import React, { useState, useEffect } from 'react';
import { Table, Button, Row, Popconfirm, message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { getDriverWorkHistory, upsertDriverWorkHistory, deleteDriverWorkHistory } from '@/service/driverArchive';
import WorkHistoryModal from './WorkHistoryModal';

const { confirm } = Modal;

/**
 * 工作履历区域属性
 */
interface WorkHistorySectionProps {
  isViewMode: boolean;
  driverId?: number | string;
}

/**
 * 工作履历区域组件
 */
const WorkHistorySection: React.FC<WorkHistorySectionProps> = ({
  isViewMode,
  driverId,
}) => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [workModalVisible, setWorkModalVisible] = useState(false);
  const [currentWorkRecord, setCurrentWorkRecord] = useState<any>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 加载数据
  const loadData = async () => {
    if (!driverId) {
      console.log('没有司机ID，跳过加载工作履历数据');
      return;
    }
    
    setLoading(true);
    try {
      const response = await getDriverWorkHistory(driverId);
      if (response.data?.success && response.data?.data) {
        setDataSource(response.data.data);
      } else {
        setDataSource([]);
      }
    } catch (error) {
      console.error('加载工作履历数据失败:', error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  // 组件创建时加载数据
  useEffect(() => {
    loadData();
  }, [driverId]);

  const handleAdd = () => {
    setCurrentWorkRecord(null);
    setWorkModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setCurrentWorkRecord(record);
    setWorkModalVisible(true);
  };

  const handleDelete = (record: any) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: '确定要删除这条工作履历记录吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deleteDriverWorkHistory(record.id);
          if (response.data?.success) {
            message.success('删除成功');
            loadData(); // 刷新列表
          } else {
            message.error(response.data?.msg || '删除失败');
          }
        } catch (error) {
          console.error('删除工作履历记录失败:', error);
          message.error('删除失败，请重试');
        }
      },
    });
  };

  const handleModalOk = async (values: any) => {
    if (!driverId) {
      message.error('缺少司机ID');
      return;
    }

    setConfirmLoading(true);
    try {
      const submitData = {
        ...values,
        driverId: driverId,
        // 如果是编辑模式，带上id
        ...(currentWorkRecord ? { id: currentWorkRecord.id } : {}),
      };

      const response = await upsertDriverWorkHistory(submitData);
      
      if (response.data?.success) {
        message.success(currentWorkRecord ? '编辑成功' : '新增成功');
        setWorkModalVisible(false);
        setCurrentWorkRecord(null);
        loadData(); // 刷新列表
      } else {
        message.error(response.data?.msg || '操作失败');
      }
    } catch (error) {
      console.error('保存工作履历记录失败:', error);
      message.error('操作失败，请重试');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleModalCancel = () => {
    setWorkModalVisible(false);
    setCurrentWorkRecord(null);
  };

  const columns: any[] = [
    { title: '工作时间', dataIndex: 'workTime', key: 'workTime' },
    { title: '公司名称', dataIndex: 'compName', key: 'compName' },
    { title: '工作类型', dataIndex: 'workType', key: 'workType' },
    { title: '工作内容', dataIndex: 'workCnt', key: 'workCnt' },
    { title: '燃油类型', dataIndex: 'fuelTp', key: 'fuelTp' },
    { title: '出险情况', dataIndex: 'accidentStat', key: 'accidentStat' },
    { title: '安全评级情况', dataIndex: 'safetyRateStat', key: 'safetyRateStat' },
    { title: '日均驾驶时长', dataIndex: 'dailyAvgDriveHr', key: 'dailyAvgDriveHr' },
    { title: '日均驾驶里程', dataIndex: 'dailyAvgDriveMile', key: 'dailyAvgDriveMile' },
    { title: '夜间驾驶比例', dataIndex: 'nightDriveRatio', key: 'nightDriveRatio' },
    {
      title: '异常驾驶行为日均次数',
      dataIndex: 'abnormalDriveDailyCnt',
      key: 'abnormalDriveDailyCnt',
    },
    {
      title: '驾驶不专注日均次数',
      dataIndex: 'inattentiveDriveDailyCnt',
      key: 'inattentiveDriveDailyCnt',
    },
    {
      title: '抽烟日均触发次数',
      dataIndex: 'smokingDailyTrig',
      key: 'smokingDailyTrig',
    },
    { title: '作业城市', dataIndex: 'opCity', key: 'opCity' },
    { title: '作业道路', dataIndex: 'opRoad', key: 'opRoad' },
    { title: '作业过的环境', dataIndex: 'operatedEnv', key: 'operatedEnv' },
    { title: '作业速度', dataIndex: 'opSpeed', key: 'opSpeed' },
  ];

  // 仅在编辑模式下添加操作列
  if (!isViewMode) {
    columns.push({
      title: '操作',
      key: 'action',
      width: 150,
      render: (text: any, record: any) => (
        <Row>
          <Button type="link" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Row>
      ),
    });
  }

  return (
    <>
      {!isViewMode && (
        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <Button onClick={handleAdd} type="primary" style={{ marginBottom: 16 }}>
            新增工作履历
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={false}
        rowKey="id"
        bordered
        scroll={{ x: 'max-content' }}
      />
      <WorkHistoryModal
        visible={workModalVisible}
        onCancel={handleModalCancel}
        onOk={handleModalOk}
        initialValues={currentWorkRecord}
        confirmLoading={confirmLoading}
      />
    </>
  );
};

export default WorkHistorySection;
